<template>
  <div v-if="object" class="w-full h-full p-3">
    <div class="bg-gradient-to-r from-blue-50 to-transparent dark:from-gray-800 dark:to-transparent rounded-xl p-6 mb-6">
      <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-3">
        {{ $t("Select RevSetter Object Type") }}
      </h2>
      <div class="space-y-2 text-gray-600 dark:text-gray-400">
        <p class="flex items-start">
          <Info class="w-5 h-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
          <span>
            {{ $t("You are mapping") }} <span class="font-medium text-gray-900 dark:text-white">{{ object.label }}</span>
          </span>
        </p>
        <p class="flex items-start">
          <FileCheck class="w-5 h-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
          <span>{{ $t("Here you can link your object to one of the available objects in the list below.") }}</span>
        </p>
      </div>
    </div>
    <div class="flex items-start justify-between mt-6 mb-2">
      <div class="relative w-full max-w-sm">
        <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
          <Search class="w-5 h-5 text-gray-400" aria-hidden="true" />
        </div>
        <input
          v-model="filter"
          autocomplete="off"
          type="text"
          class="block w-full pl-10 pr-3 py-2 text-sm border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          :placeholder="$t('Search by object name')">
      </div>
    </div>

    <transition-group name="fade" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 py-4 overflow-y-auto" tag="div">
      <div
        v-for="(obj_type, index) in filtered_loaded_object_types"
        :key="obj_type.label"
        class="group relative flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 p-6 dark:bg-dark-box-background dark:border-gray-700 animate__animated animate__fadeInUp min-h-[280px]"
        :style="{ animationDelay: Math.min(index * 0.002, 1) + 's' }">
        <div
          v-if="isObjectMappedToObjectType(object, obj_type)"
          class="absolute top-4 right-4 flex items-center gap-1.5 px-2.5 py-1 bg-green-50 rounded-full">
          <CircleCheck class="w-4 h-4 text-green-500" aria-hidden="true" />
          <span class="text-xs font-medium text-green-700">{{ $t("Mapped") }}</span>
        </div>

        <div class="flex flex-col flex-1">
          <div class="mb-4">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 transition-colors duration-200">
              {{ obj_type.label }}
            </h3>
          </div>

          <div v-if="obj_type.origins_types.filter((t) => t.origin.toLowerCase() !== 'platform').length > 0" class="flex-1">
            <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {{ $t("Mapped to") }}:
            </h4>
            <ul class="space-y-1.5">
              <li
                v-for="obj in obj_type.origins_types
                  .filter((t) => t.origin.toLowerCase() !== 'platform')
                  .sort((a, b) => b.is_direct - a.is_direct)"
                :key="obj.type"
                class="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <span class="truncate"> {{ getServiceTitle(obj.origin.replace("ml.", "").replace(/\.\d+$/, "")) }} > {{ obj.type }} </span>
                <span
                  :class="[
                    'ml-1.5 px-1.5 py-0.5 rounded text-xs font-medium',
                    obj.is_direct ? 'bg-blue-50 text-blue-700' : 'bg-gray-50 text-gray-600'
                  ]">
                  {{ obj.is_direct ? $t("Primary") : $t("Secondary") }}
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div class="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
          <button
            v-if="isChooseBtnEnabled(obj_type) || obj_type.origins_types.filter(o => o?.origin.toLowerCase() != 'platform').filter(
              (o) => (o.type == object.name || o.type == object.label) && integration.name == o.origin && o?.is_direct == true
            ).length == 0"
            type="button"
            class="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors duration-200"
            @click="chooseObjectType(obj_type.type)">
            {{ $t("Choose") }}
          </button>
        </div>
      </div>

      <div
        v-if="mappingType == 'direct'"
        key="add_custom_btn"
        class="group relative flex flex-col items-center justify-center bg-gray-50 hover:bg-gray-100 border-2 border-dashed border-gray-300 rounded-xl p-6 transition-colors duration-200 dark:bg-dark-box-background dark:border-gray-700">
        <div class="text-center space-y-4">
          <div class="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-50 text-blue-600">
            <Plus class="w-6 h-6" aria-hidden="true" />
          </div>
          <h3 class="text-lg font-medium text-gray-900 dark:text-white">
            {{ $t("Create new object") }}
            <span class="block text-sm text-gray-500 font-normal mt-1">{{ object.label }}</span>
          </h3>
          <button
            type="button"
            :disabled="hasDirectMappedObjectType(object)"
            :class="[
              'inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200',
              hasDirectMappedObjectType(object)
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            ]"
            @click="show_create_new_object = true">
            {{ $t("Add") }}
          </button>
          <p v-if="hasDirectMappedObjectType(object)" class="text-sm text-gray-500 dark:text-gray-400">
            {{ $t("This object already directly mapped to object") }} {{ getDirectMappedObjectType(object)?.name }}
          </p>
        </div>
      </div>
    </transition-group>

    <Modal v-model:is-open="show_create_new_object">
      <template #form>
        <h3 class="text-2xl font-medium leading-10 text-left text-gray-600 dark:text-white">
          {{ $t("Create New Object") }}
          <CirclePlus class="float-right w-6 h-6" color="#0284c7" aria-hidden="true" />
        </h3>
        <div class="flex flex-row justify-between flex-1 h-40 gap-2">
          <TextInput
            v-model="object_name"
            :value="object_name"
            :label="$t('Object Label')"
            name="label"
            class="mt-2"
            :validation-endpoint="`/api/organizations/${$store.state.organization.id}/object-types/check-label/`"
            classes="w-full relative dark:text-white"
            :placeholder="$t('Please enter your new object label')"
            :is-box-background="true"
            :show-label="true"
            :validate-on-input="true" />
          <TextInput
            v-model="object_plural_label"
            :value="object_plural_label"
            :label="$t('Object Plural Label')"
            name="label"
            class="mt-2"
            classes="w-full relative dark:text-white"
            :placeholder="$t('Please enter your new object label')"
            :is-box-background="true"
            :show-label="true"
            :validate-on-input="true" />
        </div>
        <div class="flex flex-row justify-between w-full gap-2 mt-2">
          <button type="button" class="w-1/2 m-0 cancel-btn" @click="show_create_new_object = false">{{ $t("Cancel") }}</button>
          <button type="button" :disabled="$utils.isEmpty(object_name)" class="w-1/2 primary-btn" @click="createNewObject">
            {{ $t("Create") }}
          </button>
        </div>
      </template>
    </Modal>
  </div>
</template>
<script>
import Modal from "@/components/shared/Modal.vue"
import { CirclePlus, Search, Plus, CircleCheck, Info, FileCheck } from "lucide-vue-next"
import TextInput from "@/components/shared/TextInput.vue"
import { utils } from "@/store/helpers"
import ObjectTypesService from "@/services/object_types.service"
import { getServiceTitle } from "@/views/dashboard/admin/integrations/ml.js"

export default {
  name: "DataObjectTypeSelect",
  components: {
    TextInput,
    Search,
    Modal,
    CircleCheck,
    Plus,
    CirclePlus,
    Info,
    FileCheck
  },
  props: {
    object: {
      type: Object,
      required: true
    },
    integration: {
      type: Object,
      required: true
    },
    mappingType: {
      type: String,
      required: true,
      default: "direct"
    }
  },
  emits: ["update:mappedObjectType"],
  data() {
    return {
      utils,
      getServiceTitle,
      object_name: null,
      object_plural_label: null,
      integration_name: null,
      filter: "",
      show_create_new_object: false,
      show_input_object_name: false,
      selected_object_type: null,
      object_types: this.$store.state.object_types
    }
  },
  computed: {
    filtered_loaded_object_types() {
      let filtered = this.filter
        ? this.object_types.filter((f) => f.label.toLowerCase().includes(this.filter.toLowerCase()))
        : this.object_types

      // Sort objects: those with choose button enabled come first
      return filtered.sort((a, b) => {
        const aEnabled = this.isChooseBtnEnabled(a)
        const bEnabled = this.isChooseBtnEnabled(b)
        if (aEnabled === bEnabled) {
          // If both have same enabled status, sort alphabetically
          return a.label.localeCompare(b.label)
        }
        // Put enabled ones first
        return aEnabled ? -1 : 1
      })
    }
  },
  activated() {
    this.object_name = this.object.label
    this.object_plural_label = utils.makePlural(this.object.label)
  },
  async created() {
    this.object_types = this.$store.state.object_types
    this.integration_name = this.$route.params.integration_name
    this.object_name = this.object.label
    this.object_plural_label = utils.makePlural(this.object.label)
  },
  methods: {
    isChooseBtnEnabled(obj_type) {
      if (this.mappingType == "direct") {
        return this.isObjectMappedToObjectType(this.object, obj_type)
      } else {
        // when the object is not directly mapped to the object type
        return (
          obj_type.origins_types.filter(o => o?.origin.toLowerCase() != 'platform').filter(
            (o) => (o.type == this.object.name || o.type == this.object.label) && o.origin == this.integration.origin && o.is_direct == true
          ).length == 0
        )
      }
    },

    hasDirectMappedObjectType(object) {
      let has_direct_mapped_object = false
      this.object_types.forEach((obj_type) => {
        if (this.isObjectMappedToObjectType(object, obj_type)) {
          has_direct_mapped_object = true
        }
      })
      return has_direct_mapped_object
    },
    getDirectMappedObjectType(object) {
      let direct_mapped_object = null
      this.object_types.forEach((obj_type) => {
        if (this.isObjectMappedToObjectType(object, obj_type)) {
          direct_mapped_object = obj_type
        }
      })
      return direct_mapped_object
    },
    isObjectMappedToObjectType(obj, obj_type) {
      return (
        obj_type.origins_types.filter(o => o?.origin.toLowerCase() != 'platform').filter(
          (o) => (o.type == obj.name || o.type == obj.label) && this.integration.name == o.origin && o?.is_direct == true
        ).length > 0
      )
    },

    chooseObjectType(object_type, is_new_object = false) {
      this.show_input_object_name = false
      this.selected_object_type = object_type
      //{ object_type, object_name, is_new_object }
      this.$emit("update:mappedObjectType", { object_type, object_name: this.object_name, is_new_object: is_new_object })
    },
    createNewObject() {
      ObjectTypesService.createObjectType({
        label: this.object_name,
        type: this.object.name,
        name: this.object.name,
        integration: this.$route.params.integration_reference,
        plural_label: this.object_plural_label
      })
        .then((res) => {
          this.chooseObjectType(res.data.type, true)
          this.show_create_new_object = false
        })
        .catch(() => {
          this.$toast.open({
            message: this.$t("Object name") + ` ${this.object_name} ` + this.$t("is already created, please choose another name"),
            position: "top",
            type: "error",
            duration: 0,
            dismissible: true
          })
        })
    }
  }
}
</script>

<style>
.list-enter-active,
.list-leave-active {
  transition: all 0.3s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateY(30px);
}

.list-move {
  transition: transform 0.3s ease;
}
</style>
